<template>
  <div>
    <naj-box :title="local.bankruptcy.attorneyForm.title">
      <v-row>
        <v-col
          cols="12"
          lg="2"
        >
          <naj-input
            :type-id="formFieldType.selectbox"
            :label="local.attorney.type"
            :value.sync="formModel.attorneyTypeId"
            :items="attorneyTypes"
            item-text="name"
            item-value="id"
            :disabled="isDisabledAttorney"
            validates="required"
            @change="attorneyTypeChanged()"
          ></naj-input>
        </v-col>
        <v-col
          v-if="formModel.attorneyTypeId"
          cols="12"
          lg="2"
        >
          <naj-input
            :type-id="formFieldType.number"
            :label="`${isInternalAttorney ? local.attorney.number : local.attorney.approvalNumber}`"
            :value.sync="formModel.attorneyNumber"
            :validates="`required|long|max:14|isValid:${local.attorney.invalidNumber},${isNumberValid}|isVerified:${isVerified}`"
            :disabled="isDisabledAttorney || !formModel.attorneyTypeId"
            @input="changeVerification(false)"
          ></naj-input>
        </v-col>
        <v-col
          cols="12"
          lg="3"
          class="d-flex justify-start align-center"
        >
          <naj-button
            v-if="!isViewMode && !isVerified"
            square
            inputs-alignment
            :loading="loading"
            :disabled="isViewMode || isVerified || !formModel.attorneyNumber || !formModel.attorneyTypeId"
            @click="validateAttorneyNumber"
          >
            {{ local.common.check }}
          </naj-button>

          <naj-button
            v-if="isVerified && formModel.attorneyNumber && formModel.attorneyTypeId"
            square
            inputs-alignment
            class="view-attorney-text"
            @click="openAttorneyModal"
          >
            {{ isInternalAttorney? local.attorney.attorneyText: local.attorney.attorneyDoc }}
          </naj-button>
        </v-col>
      </v-row>
      <v-row v-if="isExternalAttorney">
        <v-col
          cols="12"
          lg="2"
        >
          <naj-input
            :type-id="formFieldType.selectbox"
            :label="local.attorney.source"
            :value.sync="formModel.externalAttorneySourceId"
            :disabled="externalAttorneySourceDisabled"
            :items="countries"
            item-text="name"
            item-value="id"
            validates="required"
          ></naj-input>
        </v-col>

        <v-col
          cols="12"
          lg="2"
          class="align-self-center"
        >
          <naj-input
            :type-id="formFieldType.checkbox"
            :label="local.attorney.isApprovedFromMofa"
            :value.sync="formModel.isApprovedFromMofa"
            disabled
          ></naj-input>
        </v-col>
        <v-col
          cols="12"
          lg="2"
          class="align-self-center"
        >
          <naj-input
            :type-id="formFieldType.checkbox"
            :label="local.attorney.isApprovedFromMoj"
            :value.sync="formModel.isApprovedFromMoj"
            disabled
          ></naj-input>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="6">
          <audit-confirmation-flag-checkbox :audit-flag="mainRequestAuditFlag.attorneyConfirmation"></audit-confirmation-flag-checkbox>
        </v-col>
      </v-row>
    </naj-box>
    <attorney-modal
      v-if="attorneyData.attorneyCode"
      ref="viewAttorneyModal"
      :attorney-type="attorneyData.attorneyTypeId"
      :attorney-code="attorneyData.attorneyCode.toString()"
      :agent-social-id="attorneyData.agentSocialId"
      :client-social-id="attorneyData.clientSocialId"
      :attorney-details="attorneyDetails"
      :unified-number="requestOwnerUnifiedNumber"
      @onClose="onModalClose"
    ></attorney-modal>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import { partyTypeEnum } from '@/helpers/enum';
import { mainRequestAuditFlag, requestStatusEnum, attorneyTypeEnum, requestOwnerTypeEnum } from '@/modules/bankruptcy/helpers/enum';
import AuditConfirmationFlagCheckbox from '@/modules/bankruptcy/components/request/audit-confirmation-flag-checkbox';
const { mapActions, mapState: mapLUTState } = createNamespacedHelpers('BankruptcyModule');
const { mapActions: mapSysActions } = createNamespacedHelpers('SystemModule');
import AttorneyModal from '../../attorney-modal/index.vue';

const initAttorneyData = () => ({
  attorneyCode: 0,
  agentSocialId: '',
  clientSocialId: '',
  attorneyTypeId: 1,
});

export default {
  name: 'AttorneyForm',
  inject: ['$validator'],
  components: {
    AuditConfirmationFlagCheckbox,
    AttorneyModal,
  },
  props: {
    formModel: {
      type: Object,
      required: true,
    },
    identityNumber: {
      type: [Number, String],
      required: true,
    },
    auditConfirmations: {
      type: Object,
      required: true,
    },
    isNewMode: {
      type: Boolean,
      required: true,
    },
    isViewMode: {
      type: Boolean,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      required: true,
    },
    isNotCompleted: {
      type: Boolean,
      required: true,
    },
    agent: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      partyTypeEnum,
      mainRequestAuditFlag,
      requestStatusEnum,
      requestOwnerTypeEnum,
      loading: false,
      attorneyText: null,
      attorneyData: initAttorneyData(),
      attorneyDetails: {},
      externalAttorneySourceDisabled: true,
    };
  },
  computed: {
    ...mapLUTState(['attorneyTypes', 'countries']),
    isVerified() { return this.formModel.isAttorneyVerified; },
    isExternalAttorney() {
      return this.formModel.attorneyTypeId == attorneyTypeEnum.external;
    },
    isInternalAttorney() {
      return this.formModel.attorneyTypeId == attorneyTypeEnum.internal;
    },
    isNumberValid() {
      return this.formModel.attorneyNumber !== 0;
    },
    isIdentityVerified() {
      return this.agent.isIdentityVerified;
    },
    isDisabledAttorney() {
      if (this.isViewMode)
        return true;
      if (this.agent.typeId == partyTypeEnum.foreignCompany && !!this.managerIdentity) {
        return false;
      }
      if (!this.isIdentityVerified)
        return true;
      if (this.auditConfirmations[`flag_${mainRequestAuditFlag.attorneyConfirmation}`] && this.isNotCompleted)
        return true;
      return false;
    },
    partyType() {
      return this.agent.typeId;
    },
    managerIdentity() {
      return this.agent.managerIdentityNumber ? this.agent.managerIdentityNumber : this.agent.manager?.identityNumber;
    },
    requestOwnerIdentityNumber() {
      return this.agent.typeId == partyTypeEnum.person ? this.agent.identityNumber
        : this.agent.typeId == partyTypeEnum.foreignCompany ? this.managerIdentity
          : this.agent.commercialRegistrationNumber;
    },
    managerSocialId() {
      return this.agent.typeId == partyTypeEnum.firm ? this.managerIdentity : null;
    },
    requestOwnerUnifiedNumber() {
      return this.agent.typeId == partyTypeEnum.person ? this.agent.identityNumber : this.agent.unifiedNumber;
    },
  },
  watch: {
    isIdentityVerified(newValue) {
      if (!newValue) {
        this.changeVerification(false);
        this.$set(this.formModel, 'attorneyTypeId', '');
        this.$set(this.formModel, 'attorneyNumber', '');
      }
    },
    'identityNumber'() {
      this.changeVerification(false);
    },
    'agent.identityNumber'() {
      if (!this.isViewMode)
        this.changeVerification(false);
    },
    'agent.commercialRegistrationNumber'() {
      if (!this.isViewMode)
        this.changeVerification(false);
    },
    'agent.managerIdentityNumber'() {
      if (!this.isViewMode)
        this.changeVerification(false);
    },
    'agent.unifiedNumber'() {
      if (!this.isViewMode)
        this.changeVerification(false);
    },
  },
  created() {
  },
  mounted() {
    this.getCountries();
  },
  methods: {
    ...mapActions(['getCountries']),
    ...mapSysActions(['validateAgentAttorney', 'validateAgentAttorneyIncludeExternal']),
    attorneyTypeChanged() {
      this.changeVerification(false);
      this.resetExternalAttorneyDetails();
    },
    changeVerification(isVerified) {
      // if (this.isVerificationDisabled) return;
      this.$set(this.formModel, 'isAttorneyVerified', isVerified);
    },
    resetExternalAttorneyDetails() {
      this.$set(this, 'attorneyDetails', null);
      this.$set(this.formModel, 'approvalNumber', null);
      this.$set(this.formModel, 'isApprovedFromMofa', null);
      this.$set(this.formModel, 'isApprovedFromMoj', null);
      this.$set(this.formModel, 'externalAttorneySourceId', null);
      this.changeVerification(false);
    },
    validateAttorneyNumber() {
      this.loading = true;
      this.changeVerification(false);
      const payload = this.getAttorneyPayload();
      (this.isExternalAttorney ? this.validateAgentAttorneyIncludeExternal(payload) : this.validateAgentAttorney(payload))
        .then((result) => {
          if (result) {
            if (this.isExternalAttorney) {
              this.setExternalAttorneyDetails(result);
            }
            this.changeVerification(true);
            this.toaster.success(this.local.bankruptcy.common.validateSuccessfully);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    setExternalAttorneyDetails(details) {
      this.$set(this, 'attorneyDetails', details);
      this.$set(this.formModel, 'approvalNumber', details.attorneyNumber);
      this.$set(this.formModel, 'isApprovedFromMofa', details.isApprovedFromMofa);
      this.$set(this.formModel, 'isApprovedFromMoj', true);
      const attorneyCountry = (this.countries || []).find(a => a.name == details.kharjiaCountry);
      if (attorneyCountry) {
        this.externalAttorneySourceDisabled = true;
        this.$set(this.formModel, 'externalAttorneySourceId', attorneyCountry.id);
      } else {
        this.externalAttorneySourceDisabled = false;
      }
    },
    openAttorneyModal() {
      this.attorneyData = this.getAttorneyPayload();
      this.$nextTick(() => {
        this.$refs.viewAttorneyModal.show();
      });
    },
    onModalClose() {
      this.attorneyData = initAttorneyData();
    },
    getAttorneyPayload() {
      return {
        attorneyCode: this.formModel.attorneyNumber,
        agentSocialId: this.identityNumber,
        clientSocialId: this.requestOwnerIdentityNumber,
        commercialRegistrationNumber: this.agent.commercialRegistrationNumber,
        attorneyType: this.formModel.attorneyTypeId,
        managerSocialId: this.managerSocialId,
        unifiedNumber: this.requestOwnerUnifiedNumber,
      };
    },
  },

};
</script>

<style lang="scss">
</style>
